import { Component, OnInit, OnDestroy, inject, Inject } from "@angular/core";

import { Observable, Subject, takeUntil } from "rxjs";
import { delay, filter, map } from "rxjs/operators";

import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { RedirectService } from "@e-forsyning/common/router";

import { AppInfo } from "./app-info/app-info.model";
import { AppInfoService } from "./app-info/app-info.service";
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { AppShellComponent } from "@e-forsyning/common/app-shell";
import { EforsyningSidenavMenuComponent } from "@e-forsyning/common/eforsyning-menu";
import { AppVaelgInstallationComponent } from "./app-vaelg-installation/app-vaelg-installation.component";
import { AppInfoComponent } from "./app-info/app-info.component";
import { AsyncPipe, DOCUMENT } from "@angular/common";
import { NotificationPermissionRequestComponent } from "@e-forsyning/common/push-notification";
import { isTeknikerContext } from "@e-forsyning/common/util";
import { AktuelForsyningIndstillingerObserver } from "@e-forsyning/common/forsyning-indstillinger/aktuel-forsyning-indstillinger.observer";
import { FeatureFlagService } from "@e-forsyning/features/feature-flag";
import { AppReferrer } from "./app-referrer/app-referrer";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    imports: [
        AppShellComponent,
        EforsyningSidenavMenuComponent,
        AppVaelgInstallationComponent,
        AppInfoComponent,
        RouterOutlet,
        AsyncPipe,
        NotificationPermissionRequestComponent,
        AppReferrer
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    public erLoggetInd$: Observable<boolean>;
    public infos$: Observable<AppInfo[]>;
    public featureFlagEnabled: boolean = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly credentials: AppserverInstallationCredentialsRepository,
        private readonly appInfoService: AppInfoService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly title: Title,
        private readonly redirectService: RedirectService,
        private readonly aktuelForsyningIndstillinger: AktuelForsyningIndstillingerObserver,
        private readonly featureFlagService: FeatureFlagService,
        @Inject(DOCUMENT) private readonly document: Document
    ) {}

    public ngOnInit(): void {
        const isTeknikerMode = isTeknikerContext();
        this.erLoggetInd$ = this.credentials.valueChanges.pipe(
            delay(0) /* Lav fra Synkron til Asynkron for at undgå: ExpressionChangedAfterItHasBeenCheckedError */,
            map((credentials) => !!credentials.forsyningNodeId)
        );
        this.infos$ = this.appInfoService
            .getInfos(isTeknikerMode)
            .pipe(delay(0) /* Lav fra Synkron til Asynkron for at undgå: ExpressionChangedAfterItHasBeenCheckedError */);

        this.setPageTitle();
        this.applyTeknikerTheme(isTeknikerMode);

        this.aktuelForsyningIndstillinger.isPilot.pipe(takeUntil(this.destroy$)).subscribe((isPilot) => {
            this.featureFlagEnabled = isPilot;
            this.featureFlagService.setPilotKundeToggle(isPilot);
        });

        //  Matomo is now initialized with the correct site ID from UmbracoIndstillinger
        //  No need to update it dynamically
    }

    public setPageTitle() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                    const child: ActivatedRoute | null = this.route.firstChild;
                    const documentid = this.router.url.split("/mine-dokumenter/").pop();
                    let title = child && child.snapshot.data["title"];
                    if (this.router.url.includes("/mine-dokumenter/") && documentid && title) {
                        return `${title} - ${documentid}`;
                    } else if (title) {
                        return title;
                    }
                })
            )
            .subscribe((title) => {
                if (title) {
                    this.title.setTitle(`${title} - E|Forsyning`);
                }
            });
    }

    public onInstallationerClick(): void {
        this.redirectService.redirectTilInstallationer();
    }

    public onCloseAppInfo(value: AppInfo): void {
        value.visible = false;
        this.appInfoService.onCloseAppInfo(value);
    }

    private applyTeknikerTheme(isTeknikerMode: boolean): void {
        if (typeof window === "undefined" || !this.document) {
            return;
        }
        this.document.body.classList.toggle("tekniker-app", isTeknikerMode);
        this.document.documentElement.classList.toggle("tekniker-app", isTeknikerMode);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
